import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Radio from "~/assets/js/components/Forms/Radio";
import i18n from "~/assets/js/Helpers/i18n";
import Popup from "~/assets/js/components/Popup";
import Button from "~/assets/js/components/Button";
import { convertUnit } from "~/assets/js/Format";
import useViewport from "~/assets/js/Helpers/hooks/useViewport";
import { CheckoutContext } from "~/assets/js/pages/Checkout/CheckoutProvider";
import { HelpCircle } from "react-feather";
import Headline from "~/assets/js/components/Typography/Headline";
import { useCartContext } from "~/assets/js/global/cartContext";
import { formatPrice } from "../../Format";

const EnvCompensation = ({ setCompensation }) => {
    const { cart } = useCartContext();
    const co2 = cart?.co2 || 0;
    const water = cart?.water || 0;

    const { state: contextState } = useContext(CheckoutContext);
    const [state, setState] = useState({
        selected: "none",
        climateDetails: [],
        singlePrice: cart?.compensation?.predicted_values?.single,
        doublePrice: cart?.compensation?.predicted_values?.double,
        openInfo: false,
    });

    const { isDesktop } = useViewport();

    const handleChangeValue = (value) => {
        setState({ ...state, selected: value });
        setCompensation(value);
    };

    useEffect(() => {
        let formatedClimateDetails = [];

        if (co2 !== 0) {
            formatedClimateDetails.push({
                value: handleUnitConversion(co2),
                unit: "kg",
                climate: "CO2e",
            });
        }

        if (water !== 0) {
            formatedClimateDetails.push({
                value: handleUnitConversion(water),
                unit: "l",
                climate: "Wasser",
            });
        }

        setState({
            ...state,
            climateDetails: formatedClimateDetails,
            singlePrice: cart?.compensation?.predicted_values?.single,
            doublePrice: cart?.compensation?.predicted_values?.double,
        });
    }, [cart]);

    const formatClimateItems = (items) => {
        return (
            <>
                &nbsp;
                {items.map((item, i) => {
                    const isLastItem =
                        i === items.length - 1 && items.length > 1;
                    if (isLastItem) {
                        return (
                            <span key={i}>und{formatClimateItem(item)}</span>
                        );
                    }
                    return formatClimateItem(item);
                })}
            </>
        );
    };

    const formatClimateItem = (item) => {
        if (item.climate === "CO2e") {
            return (
                <strong key={item.climate}>
                    {`${item.value}${item.unit}`}&nbsp;
                    <CO2 /> &nbsp;
                </strong>
            );
        }
        const formatedItemText = `${item.value}${item.unit} ${item.climate}`;
        return <strong key={item.climate}>&nbsp;{formatedItemText}</strong>;
    };

    const handleInfoPopup = () => {
        setState({ ...state, openInfo: !state.openInfo });
    };

    const { page } = contextState;

    const hideOnDesktop =
        isDesktop && !["cart", "orderOverview"].includes(page);
    const hasNoEnvCompensateValues = state.climateDetails.length === 0;

    if (hasNoEnvCompensateValues || hideOnDesktop) {
        return null;
    }

    return (
        <>
            <div className="env-compensation">
                <div className="env-compensation__header">
                    <h3>{i18n.t("env_compensation.title")}</h3>
                    <span
                        onClick={handleInfoPopup}
                        className="env-compensation__header__info"
                    >
                        <HelpCircle />
                        {isDesktop && i18n.t("env_compensation.info.cta")}
                    </span>
                </div>
                <p className="env-compensation__summary">
                    {i18n.t("env_compensation.summary")}
                    {formatClimateItems(state.climateDetails)}
                </p>
                <hr className="summary__divider" />
                <form>
                    <Radio
                        customStyles="env-compensation__option"
                        bindName="env_compensate"
                        value="single"
                        onValueChange={() => handleChangeValue("single")}
                        currentValue={cart?.compensation.level}
                    >
                        <div className="env-compensation__option-wrapper">
                            <span className="env-compensation__option-label">
                                {i18n.t("env_compensation.options.single")}
                            </span>
                            <span className="env-compensation__option-price">
                                {i18n.t("env_compensation.options.price", {
                                    price: formatPrice(
                                        state.singlePrice?.value_gross
                                    ),
                                    tax: formatPrice(
                                        state.singlePrice?.value_tax
                                    ),
                                })}
                            </span>
                        </div>
                    </Radio>
                    <Radio
                        customStyles="env-compensation__option"
                        bindName="env_compensate"
                        value="double"
                        onValueChange={() => handleChangeValue("double")}
                        currentValue={cart?.compensation.level}
                    >
                        <div className="env-compensation__option-wrapper">
                            <span className="env-compensation__option-label">
                                {i18n.t("env_compensation.options.double")}
                            </span>
                            <span className="env-compensation__option-price">
                                {i18n.t("env_compensation.options.price", {
                                    price: formatPrice(
                                        state.doublePrice?.value_gross
                                    ),
                                    tax: formatPrice(
                                        state.doublePrice?.value_tax
                                    ),
                                })}
                            </span>
                        </div>
                    </Radio>
                    <Radio
                        customStyles="env-compensation__option"
                        bindName="env_compensate"
                        value="none"
                        onValueChange={() => handleChangeValue("none")}
                        currentValue={cart?.compensation.level}
                    >
                        <div className="env-compensation__option-wrapper">
                            <span className="env-compensation__option-label">
                                {i18n.t("env_compensation.options.none")}
                            </span>
                        </div>
                    </Radio>
                </form>
            </div>
            <Popup open={state.openInfo}>
                <HelpCircle
                    size={48}
                    scale={2}
                    className="env-compensation__info-icon"
                />
                <Headline size={5} className="env-compensation__info-title">
                    {i18n.t("env_compensation.info.title")}
                </Headline>
                <p className="env-compensation__info-text">
                    {i18n.t("env_compensation.info.text")}
                </p>
                <p className="env-compensation__info-text">
                    {i18n.t("env_compensation.info.textBeforeLink")}
                    <a href={i18n.t("env_compensation.info.link")}>
                        {i18n.t("env_compensation.info.linkText")}
                    </a>
                </p>

                <Button
                    variant="secondary"
                    size="SM"
                    onClick={handleInfoPopup}
                    label={i18n.t("env_compensation.info.button")}
                />
            </Popup>
        </>
    );
};

const CO2 = () => (
    <>
        CO<sup>2</sup>e
    </>
);

const handleUnitConversion = (value) => {
    const multiplier = 0.001; // factor for g -> kg && ml -> l
    return convertUnit(value, multiplier);
};

EnvCompensation.propTypes = {
    setCompensation: PropTypes.func,
};

export default EnvCompensation;
